import React, { useState, useCallback } from 'react';
import { Row, Col, Slider, Modal, useModal, Typography, Button, Box } from '@lucid/lego';
import type { FormField } from '@lucid/lego';
import type { Theme, VehicleData } from '../types';
import { useFileUpload } from '../../../hooks/useFileUpload';
import AssetOverview from '../AssetOverview/AssetOverview';
import CustomerProfile from '../CustomerProfile/CustomerProfile';
import Form from '../Form/Form';
import styles from './VehicleDetailsSlider.module.scss';

type VehicleDetailsSliderProps = {
  /** Whether the slider is open */
  isOpen: boolean;
  /** Callback when slider closes */
  onClose: () => void;
  /** Title for the slider header */
  title: string;
  /** Theme for the component */
  theme: Theme;
  /** Selected vehicle asset */
  selectedAsset: VehicleData | null;
  /** Form fields to render in the slider (optional - no form if not provided) */
  formFields?: FormField[];
  /** Title for the form accordion */
  formTitle?: string;
  /** Form ID */
  formId?: string;
  /** Success message to display */
  successMessage?: string;
  /** Error message to display */
  errorMessage?: string | null;
  /** Whether form is submitting */
  isSubmitting?: boolean;
  /** Callback when form submits successfully */
  onFormSuccess?: (data: unknown) => void;
  /** Callback when form has validation errors */
  onFormError?: (error: unknown) => void;
  /** Ref for scrolling to top */
  containerRef?: React.RefObject<HTMLDivElement | null>;
};

/**
 * Vehicle details slider with asset overview, customer profile, and optional form
 * @component
 * @param {VehicleDetailsSliderProps} props - Component props
 * @returns {JSX.Element} Slider panel with vehicle details and optional form
 * @example
 * <VehicleDetailsSlider
 *   isOpen={true}
 *   onClose={handleClose}
 *   title="Vehicle Details"
 *   theme="dark"
 *   selectedAsset={vehicle}
 *   formFields={fields}
 *   onFormSuccess={handleSuccess}
 *   onFormError={handleError}
 * />
 */
const VehicleDetailsSlider: React.FC<VehicleDetailsSliderProps> = ({
  isOpen,
  onClose,
  title,
  theme,
  selectedAsset,
  formFields,
  formTitle = 'Form Details',
  formId = 'vin-search-form',
  successMessage = '',
  errorMessage = null,
  isSubmitting = false,
  onFormSuccess,
  onFormError,
  containerRef,
}) => {
  const hasForm = formFields && formFields.length > 0 && onFormSuccess && onFormError;
  const confirmModal = useModal();
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';
  const [pendingClose, setPendingClose] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  // File upload
  const { getUploadUrl, handleFileRemove } = useFileUpload({
    customerId: selectedAsset?.customerIdentifier || 'unknown',
    category: 'generic',
    tags: { vin: selectedAsset?.details.vin || '' },
  });

  const handleUploadComplete = useCallback((_fileUrl: string, _file: File) => {
    // File uploaded successfully
  }, []);

  const handleSliderClose = () => {
    if (hasForm && isFormDirty) {
      setPendingClose(true);
      confirmModal.openModal();
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    confirmModal.closeModal();
    setPendingClose(false);
    setIsFormDirty(false);
    onClose();
  };

  const handleCancelClose = () => {
    confirmModal.closeModal();
    setPendingClose(false);
  };

  return (
    <>
      <Slider
        isOpen={isOpen}
        onClose={handleSliderClose}
        title={title}
        width="1000px"
        direction="right"
        theme={theme}
      >
        {selectedAsset && (
          <Row>
            <Col sm={12} md={12}>
              <AssetOverview asset={selectedAsset} theme={theme} />
            </Col>

            <Col sm={12} md={12}>
              <CustomerProfile customer={selectedAsset.customer} theme={theme} />
            </Col>

            {hasForm && (
              <Col sm={12} md={12}>
                <Form
                  theme={theme}
                  formFields={formFields}
                  title={formTitle}
                  formId={formId}
                  successMessage={successMessage}
                  errorMessage={errorMessage}
                  isSubmitting={isSubmitting}
                  onSuccess={onFormSuccess}
                  onError={onFormError}
                  onDirtyChange={setIsFormDirty}
                  containerRef={containerRef}
                  getUploadUrl={getUploadUrl}
                  onUploadComplete={handleUploadComplete}
                  onFileRemove={handleFileRemove}
                  showDevResponse
                />
              </Col>
            )}
          </Row>
        )}
      </Slider>

      <Modal
        isOpen={confirmModal.isOpen && pendingClose}
        onClose={handleCancelClose}
        title="Confirm Close"
        theme={invertedTheme}
        size="small"
        dataTestId="close-confirmation-modal"
        footer={
          <Box className={styles.modalFooter}>
            <Button
              label="No"
              type="secondary"
              color="neutral"
              theme={invertedTheme}
              handleClick={handleCancelClose}
              gaComponent="close-confirmation-no"
              dataTestId="close-confirmation-no"
            />
            <Button
              label="Yes"
              type="primary"
              color="neutral"
              theme={invertedTheme}
              handleClick={handleConfirmClose}
              gaComponent="close-confirmation-yes"
              dataTestId="close-confirmation-yes"
            />
          </Box>
        }
      >
        <Typography variant="body" size="medium" theme={invertedTheme} color="white">
          Are you sure you want to close? Any unsaved changes will be lost.
        </Typography>
      </Modal>
    </>
  );
};

export default VehicleDetailsSlider;
